import { useCallback, useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { getHistoryPosition } from '../../utils/common/history';
import {
  DASHBOARD_HREF,
  SETTINGS_HREF,
  SIGN_IN_HREF,
  SIGN_UP_HREF,
} from '../../utils/common/hrefs';

/**
 * Whether a path is the settings modal's, which opens over the page it was opened from.
 * Bounded at a path separator, so a route merely starting with the same letters is an
 * ordinary page.
 */
export const isSettingsPath = (pathname: string) =>
  pathname === SETTINGS_HREF || pathname.startsWith(`${SETTINGS_HREF}/`);

/** Pages that exist to be left, so settings never reopen over one. */
const AUTH_PATHS = [SIGN_IN_HREF, SIGN_UP_HREF];

/**
 * Whether settings are open, the page they opened over, and how to close them.
 * The page is held here rather than in history state, which every plain link
 * inside the panel would drop.
 */
const useSettingsBackground = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isSettings = isSettingsPath(location.pathname);

  // Where closing lands when the modal was opened by its own URL.
  const background = useRef(DASHBOARD_HREF);
  const backgroundPosition = useRef<number | null>(null);
  const isClosing = useRef(false);

  useEffect(() => {
    isClosing.current = false;

    if (!isSettings && !AUTH_PATHS.includes(location.pathname)) {
      background.current = `${location.pathname}${location.search}`;
      backgroundPosition.current = getHistoryPosition();
    }
  }, [isSettings, location]);

  /**
   * Opening settings pushes an entry, and each page opened inside the panel
   * pushes another. Closing pops all of them, so the reader lands on the page
   * they opened settings over rather than on a second copy of it — which would
   * leave that page's own back button stepping onto itself, or back into the
   * panel. A session that began inside the panel has no such page to return
   * to, so it replaces the entry it is on with somewhere to land, leaving the
   * panel pages the reader really did visit behind them.
   *
   * A pop is applied asynchronously, so the modal is still mounted and its
   * close controls still live while one is in flight. Each traversal resolves
   * its delta against wherever the last one left off, so a second close would
   * travel the same distance again, past the page the reader opened settings
   * over. The latch holds until the location it moved to arrives.
   */
  const closeSettings = useCallback(() => {
    if (isClosing.current) {
      return;
    }
    isClosing.current = true;

    const openedOver = backgroundPosition.current;
    const current = getHistoryPosition();

    if (openedOver !== null && current !== null && current > openedOver) {
      navigate(openedOver - current);
      return;
    }

    navigate(background.current, { replace: true });
  }, [navigate]);

  return { isSettings, background: background.current, closeSettings };
};

export default useSettingsBackground;
